import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FaCheckCircle, FaExclamationCircle, FaSpinner, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { API_URL } from '../../config';

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f8fa;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 24px;
  color: #333;
  margin-bottom: 20px;
`;

const Message = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const Icon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  color: ${props => props.success ? '#52c41a' : props.error ? '#f5222d' : '#1890ff'};
`;

const Button = styled.button`
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  &:hover {
    background-color: #40a9ff;
  }
  
  &:disabled {
    background-color: #bfbfbf;
    cursor: not-allowed;
  }
`;

const Form = styled.form`
  width: 100%;
  text-align: left;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 16px;
  
  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  
  &:hover {
    color: #666;
  }
`;

const ErrorText = styled.div`
  color: #f5222d;
  font-size: 14px;
  margin-top: 5px;
`;

const PasswordStrength = styled.div`
  height: 5px;
  margin-top: 8px;
  border-radius: 2px;
  background-color: ${props => {
    if (props.score === 0) return '#e9e9e9';
    if (props.score === 1) return '#f5222d';
    if (props.score === 2) return '#fa8c16';
    if (props.score === 3) return '#faad14';
    return '#52c41a';
  }};
`;

const PasswordRequirements = styled.ul`
  margin-top: 8px;
  padding-left: 20px;
  font-size: 14px;
  color: #666;
`;

const Requirement = styled.li`
  margin-bottom: 4px;
  color: ${props => props.met ? '#52c41a' : '#666'};
`;

const Spinner = styled.span`
  display: inline-block;
  margin-right: 8px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SetPasswordPage = () => {
  // Get token from URL
  const token = window.location.hash.match(/\/activate\/(.+)/) ? 
    window.location.hash.match(/\/activate\/(.+)/)[1] : 
    new URLSearchParams(window.location.search).get('token');
  
  const [status, setStatus] = useState('loading'); // loading, form, success, error
  const [message, setMessage] = useState('Verifying your account...');
  const [userData, setUserData] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordScore, setPasswordScore] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Password requirements
  const requirements = [
    { id: 'length', label: 'At least 8 characters long', test: pwd => pwd.length >= 8 },
    { id: 'lowercase', label: 'Contains lowercase letters', test: pwd => /[a-z]/.test(pwd) },
    { id: 'uppercase', label: 'Contains uppercase letters', test: pwd => /[A-Z]/.test(pwd) },
    { id: 'number', label: 'Contains numbers', test: pwd => /[0-9]/.test(pwd) },
    { id: 'special', label: 'Contains special characters', test: pwd => /[^A-Za-z0-9]/.test(pwd) }
  ];
  
  // Check password strength
  const checkPasswordStrength = (pwd) => {
    if (!pwd) return 0;
    
    // Count how many requirements are met
    const metRequirements = requirements.filter(req => req.test(pwd)).length;
    
    // Score based on met requirements
    return Math.min(Math.floor(metRequirements * 1.2), 4);
  };
  
  useEffect(() => {
    // Verify token and get user data
    const verifyToken = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No activation token provided. Please check your email for the activation link.');
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/auth/verify-token/${token}`);
        
        if (response.data.success) {
          setStatus('form');
          setUserData(response.data.user);
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Invalid or expired token. Please request a new activation link.');
        }
      } catch (error) {
        setStatus('error');
        setMessage(
          error.response?.data?.message || 
          'An error occurred while verifying your token. Please try again or request a new activation link.'
        );
      }
    };
    
    verifyToken();
  }, [token]);
  
  // Update password score when password changes
  useEffect(() => {
    setPasswordScore(checkPasswordStrength(password));
  }, [password]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (passwordScore < 3) {
      newErrors.password = 'Password is too weak';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (confirmPassword !== password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/auth/set-password`, {
        token,
        password
      });
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message || 'Your password has been set successfully! You can now log in.');
      } else {
        setErrors({ form: response.data.message || 'Failed to set your password. Please try again.' });
      }
    } catch (error) {
      setErrors({ 
        form: error.response?.data?.message || 'An error occurred. Please try again later.' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <Icon>
              <FaSpinner className="fa-spin" />
            </Icon>
            <Title>Verifying Your Account</Title>
            <Message>{message}</Message>
          </>
        );
        
      case 'form':
        return (
          <>
            <Icon>
              <FaLock />
            </Icon>
            <Title>Set Your Password</Title>
            <Message>
              {userData?.email ? `Welcome ${userData.email}! ` : ''}
              Please create a strong password for your account.
            </Message>
            
            {errors.form && (
              <ErrorText style={{ marginBottom: '20px' }}>{errors.form}</ErrorText>
            )}
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="password">New Password</Label>
                <InputWrapper>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </InputWrapper>
                <PasswordStrength score={passwordScore} />
                {errors.password && <ErrorText>{errors.password}</ErrorText>}
                
                <PasswordRequirements>
                  {requirements.map(req => (
                    <Requirement key={req.id} met={req.test(password)}>
                      {req.label}
                    </Requirement>
                  ))}
                </PasswordRequirements>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <InputWrapper>
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </InputWrapper>
                {errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
              </FormGroup>
              
              <Button type="submit" disabled={loading}>
                {loading && <Spinner><FaSpinner /></Spinner>}
                {loading ? 'Setting Password...' : 'Set Password'}
              </Button>
            </Form>
          </>
        );
        
      case 'success':
        return (
          <>
            <Icon success>
              <FaCheckCircle />
            </Icon>
            <Title>Password Set Successfully!</Title>
            <Message>{message}</Message>
            <Button onClick={() => window.location.hash = '#/login'}>
              Go to Login
            </Button>
          </>
        );
        
      case 'error':
        return (
          <>
            <Icon error>
              <FaExclamationCircle />
            </Icon>
            <Title>Verification Failed</Title>
            <Message>{message}</Message>
            <Button onClick={() => window.location.hash = '#/'}>
              Go to Home
            </Button>
          </>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Container>
      <Card>
        {renderContent()}
      </Card>
    </Container>
  );
};

export default SetPasswordPage;
